import { useState, useEffect, useCallback } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import type { ApiKey, ApiKeyCreated } from "../types/api";
import CreateApiKeyModal from "../components/CreateApiKeyModal";
import ApiKeySecretModal from "../components/ApiKeySecretModal";
import ApiKeyUsageModal from "../components/ApiKeyUsageModal";
import ApiKeyTable from "../components/ApiKeyTable";
import KeyChainCard from "../components/KeyChainCard";
import CreatedByFilterSelect from "../components/CreatedByFilterSelect";
import { creatorLabel } from "../lib/creator";
import {
  emptyMeText,
  matchesFilter,
  readCreatedByFilter,
  writeCreatedByFilter,
  type CreatedByFilter,
} from "../lib/createdByFilter";
import {
  chainDeleteConfirmation,
  deleteErrorText,
  deleteSecrets,
  secretDeleteConfirmation,
} from "../lib/deleteChain";
import { buildKeyChains, chainLastUsedLabel, type KeyChain } from "../lib/keyChains";
import { oldSecretWarnings } from "../lib/oldSecretWarning";
import CopyButton from "../components/CopyButton";
import ViewToggle from "../components/ViewToggle";
import { readKeyView, writeKeyView, type KeyView } from "../lib/viewPreference";

export default function ApiKeysPage() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  // Track which key's usage modal to show
  const [usageKey, setUsageKey] = useState<{
    clientId: string;
    name: string;
  } | null>(null);
  // Track which keys are being deleted (with optional error message)
  const [deletingKeys, setDeletingKeys] = useState<Map<string, string | null>>(
    new Map(),
  );
  // Track newly created key for entrance animation
  const [newlyCreatedKeyId, setNewlyCreatedKeyId] = useState<string | null>(
    null,
  );
  const [usageTrackedSince, setUsageTrackedSince] = useState<string | null>(null);
  // "Last used" is relative to when the list was loaded.
  const [now, setNow] = useState(() => new Date());
  const [view, setView] = useState<KeyView>(() => readKeyView());
  const [createdBy, setCreatedBy] = useState<CreatedByFilter>(() => readCreatedByFilter());

  const loadApiKeys = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.listApiKeys();
      setApiKeys(response.items);
      setUsageTrackedSince(response.usage_tracked_since ?? null);
      setNow(new Date());
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError("Please sign in to view API keys");
        } else {
          setError(err.message);
        }
      } else {
        setError("Failed to load API keys");
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional fetch-on-auth-ready; loadApiKeys sets loading state synchronously
      loadApiKeys();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, loadApiKeys]);

  const handleCreateSuccess = (key: ApiKeyCreated) => {
    // Optimistically add the new key to the list (without secret)
    const newApiKey: ApiKey = {
      id: key.id,
      display_name: key.display_name,
      description: key.description,
      client_id: key.client_id,
      status: key.status,
      created_at: key.created_at,
      expires_at: key.expires_at,
      hint: key.hint,
      rotated_from_key_id: key.rotated_from_key_id,
      last_used_at: null,
      successor_key_id: null,
      created_by: user ? { id: user.user_id, name: user.name } : null,
    };
    setApiKeys((prev) => [newApiKey, ...prev]);
    setCreatedKey(key);
    setShowCreateModal(false);

    // Track as newly created for animation, then clear after animation completes
    setNewlyCreatedKeyId(key.id);
    setTimeout(() => setNewlyCreatedKeyId(null), 600);
  };

  const runDelete = async (ids: string[]) => {
    setDeletingKeys((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => next.set(id, null));
      return next;
    });
    const outcome = await deleteSecrets(ids, (id) => api.deleteApiKey(id));
    // A brief fade before the deleted secrets leave the list.
    setTimeout(() => {
      setApiKeys((prev) => prev.filter((k) => !outcome.deleted.includes(k.id)));
      setDeletingKeys((prev) => {
        const next = new Map(prev);
        outcome.deleted.forEach((id) => next.delete(id));
        outcome.failed.forEach(({ id, message }) => next.set(id, message));
        return next;
      });
    }, 200);
  };

  const handleDeleteChain = (chain: KeyChain) => {
    if (confirm(chainDeleteConfirmation(chain))) void runDelete(chain.secrets.map((s) => s.id));
  };

  const handleDeleteSecret = (secret: ApiKey) => {
    if (confirm(secretDeleteConfirmation(secret))) void runDelete([secret.id]);
  };

  const clearDeleteErrors = (chain: KeyChain) => {
    setDeletingKeys((prev) => {
      const next = new Map(prev);
      chain.secrets.forEach((s) => next.delete(s.id));
      return next;
    });
  };

  const chooseCreatedBy = (next: CreatedByFilter) => {
    setCreatedBy(next);
    writeCreatedByFilter(next);
  };

  const chooseView = (next: KeyView) => {
    setView(next);
    writeKeyView(next);
  };

  // Show loading state while auth is initializing or in progress
  if (authLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-accent border-t-transparent"></div>
        <p className="mt-2 text-gray-500">Loading...</p>
      </div>
    );
  }

  const canManage = user?.can_manage_api_keys ?? false;
  const userId = user?.user_id ?? null;
  const chains = buildKeyChains(apiKeys);
  const shown = chains.filter((chain) => matchesFilter(chain, createdBy, userId));
  const isChainDeleting = (chain: KeyChain) => chain.secrets.some((s) => deletingKeys.get(s.id) === null);

  return (
    <div>
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">API Keys</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage API keys for OAuth2 client credentials authentication
            {user && (
              <span className="ml-1">
                • Company: <strong>{user.company_id}</strong>
              </span>
            )}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          {!loading && apiKeys.length > 0 && (
            <CreatedByFilterSelect value={createdBy} onChange={chooseCreatedBy} />
          )}
          <ViewToggle view={view} onChange={chooseView} />
          {canManage && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-subtle text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
            >
              <svg
                className="-ml-1 mr-2 h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create API Key
            </button>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-accent border-t-transparent"></div>
          <p className="mt-2 text-gray-500">Loading API keys...</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={loadApiKeys}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API keys list */}
      {!loading && !error && (
        <>
          {/* A company's keys share one app registration, so one client id. */}
          {apiKeys.length > 0 && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-card">
              <span className="flex-shrink-0 text-xs uppercase tracking-wide text-gray-400">
                Client ID
              </span>
              <span
                className="min-w-0 truncate font-mono text-sm text-gray-700"
                title={apiKeys[0].client_id}
              >
                {apiKeys[0].client_id}
              </span>
              <CopyButton text={apiKeys[0].client_id} title="Copy Client ID" />
              <span className="ml-auto hidden flex-shrink-0 text-xs text-gray-400 sm:inline">
                Shared by every key
              </span>
            </div>
          )}
          {apiKeys.length > 0 && createdBy === "me" && (
            <p className="mb-4 text-sm text-gray-500">
              Showing {shown.length} of {chains.length} keys ·{" "}
              <button
                type="button"
                onClick={() => chooseCreatedBy("anyone")}
                className="font-medium text-accent hover:text-accent-dark"
              >
                Show all
              </button>
            </p>
          )}
          {apiKeys.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow-card border border-gray-100">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No API keys
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {canManage
                  ? "Get started by creating a new API key."
                  : "No API keys have been created for your company yet."}
              </p>
              {canManage && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-subtle text-sm font-medium text-white bg-accent hover:bg-accent-dark transition-colors"
                  >
                    <svg
                      className="-ml-1 mr-2 h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Create API Key
                  </button>
                </div>
              )}
            </div>
          ) : shown.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow-card border border-gray-100">
              <p className="text-sm text-gray-500">
                {emptyMeText(chains)}
              </p>
              <button
                type="button"
                onClick={() => chooseCreatedBy("anyone")}
                className="mt-2 text-sm font-medium text-accent hover:text-accent-dark"
              >
                Show all
              </button>
            </div>
          ) : view === "table" ? (
            <ApiKeyTable
              chains={shown}
              userId={userId}
              usageTrackedSince={usageTrackedSince}
              now={now}
              deletingKeys={deletingKeys}
              newlyCreatedKeyId={newlyCreatedKeyId}
              onDeleteChain={canManage ? handleDeleteChain : undefined}
              onDeleteSecret={canManage ? handleDeleteSecret : undefined}
              onClearError={clearDeleteErrors}
              onShowUsage={(clientId, name) => setUsageKey({ clientId, name })}
            />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {shown.map((chain) => {
                const deleteError = deleteErrorText(chain, deletingKeys);
                return (
                  <KeyChainCard
                    key={chain.id}
                    chain={chain}
                    creator={creatorLabel(chain.createdBy, userId)}
                    lastUsed={chainLastUsedLabel(chain, usageTrackedSince, now)}
                    warnings={oldSecretWarnings(chain, usageTrackedSince, now)}
                    usageTrackedSince={usageTrackedSince}
                    now={now}
                    isDeleting={isChainDeleting(chain) && !deleteError}
                    deleteError={deleteError}
                    isNew={chain.secrets.some((s) => s.id === newlyCreatedKeyId)}
                    onDeleteChain={canManage ? handleDeleteChain : undefined}
                    onDeleteSecret={canManage ? handleDeleteSecret : undefined}
                    onClearError={clearDeleteErrors}
                    onShowUsage={(clientId, name) => setUsageKey({ clientId, name })}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Usage Modal */}
      {usageKey && (
        <ApiKeyUsageModal
          clientId={usageKey.clientId}
          keyName={usageKey.name}
          onClose={() => setUsageKey(null)}
        />
      )}

      {/* Create API Key Modal */}
      {showCreateModal && canManage && (
        <CreateApiKeyModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Show secret after creation */}
      {createdKey && (
        <ApiKeySecretModal
          apiKey={createdKey}
          onClose={() => setCreatedKey(null)}
        />
      )}
    </div>
  );
}
